import type { PerpsOnlyStatus } from 'lib/perpsOnlyStatus';

export const CHECKOUT_FAILURE_PATH = '/checkout/failure';

/**
 * Shown when a 3DS challenge URL is refused.
 *
 * The provider's own failure fields are only populated for real declines, so a rejected redirect
 * has no copy of its own and would otherwise render an empty failure screen.
 */
/** Distinguishes this failure from a real provider decline in tracking and Datadog. */
export const CHECKOUT_3DS_REJECTED_CODE = 'checkout_3ds_redirect_rejected';
export const CHECKOUT_3DS_FAILURE_TITLE = 'Payment failed';
export const CHECKOUT_3DS_FAILURE_DESCRIPTION = "We couldn't verify this card. Please try again or use another card.";

/**
 * Registrable domain Checkout serves its hosted 3DS challenge from.
 *
 * Deliberately the domain rather than an exact-origin allowlist. Checkout's challenge redirect is
 * not always on `api.checkout.com` / `api.sandbox.checkout.com` -- `3ds2.*` and other
 * per-environment subdomains are used too -- so pinning two exact origins risks rejecting a
 * legitimate challenge and failing every 3DS card deposit, which is a far worse outcome than the
 * hardening is worth. Matching the domain still blocks an arbitrary attacker host.
 */
const TRUSTED_CHECKOUT_3DS_DOMAIN = 'checkout.com';

function isTrustedCheckout3DSHost(hostname: string): boolean {
    // Suffix match on a dot boundary so `checkout.com.attacker.example` cannot pass.
    return hostname === TRUSTED_CHECKOUT_3DS_DOMAIN || hostname.endsWith(`.${TRUSTED_CHECKOUT_3DS_DOMAIN}`);
}

/**
 * The 3DS challenge URL to navigate to, or `undefined` if it is not safe to follow.
 *
 * `3DSlink` comes from Kalshi's own backend, so this is defence in depth rather than a live
 * vulnerability. The scheme check is the part that matters: `window.location.href` will happily
 * execute a `javascript:` URL, so anything non-HTTPS is refused outright.
 */
export function getTrustedCheckout3DSUrl(rawUrl: string): string | undefined {
    let url: URL;
    try {
        url = new URL(rawUrl);
    } catch {
        // Also catches a protocol-relative `//host/path`, which has no base here.
        console.error('Rejected an unparseable Checkout 3DS redirect URL');
        return undefined;
    }

    if (url.protocol !== 'https:' || !isTrustedCheckout3DSHost(url.hostname)) {
        // `error`, not `warn`: either the backend is returning something it should not, or this
        // allowlist is too narrow and is breaking real deposits. Both need someone to look, and a
        // silent failure here surfaces to the user only as a generic "Payment failed".
        console.error(`Rejected an untrusted Checkout 3DS redirect host: ${url.protocol}//${url.hostname}`);
        return undefined;
    }

    return url.href;
}

export function canProcessCheckoutCallback(params: {
    hasCheckoutCallback: boolean;
    isLoggedIn: boolean;
    userId: string;
    accountPolicyStatus: PerpsOnlyStatus;
}): boolean {
    return params.hasCheckoutCallback && params.isLoggedIn && params.userId !== '';
}

export function canProcessStripeWithdrawalCallback(params: {
    stripeUserId: string | undefined;
    isLoggedIn: boolean;
    userId: string;
    accountPolicyStatus: PerpsOnlyStatus;
}): boolean {
    return !!params.stripeUserId && params.isLoggedIn && params.stripeUserId === params.userId;
}

export function buildCheckoutFailureUrl(baseUrl: string): string {
    return `${baseUrl.replace(/\/+$/, '')}${CHECKOUT_FAILURE_PATH}`;
}

export function isCheckoutFailurePathname(pathname: string): boolean {
    const normalizedPathname = pathname.toLowerCase();

    return (
        normalizedPathname === CHECKOUT_FAILURE_PATH ||
        // Keep previously issued Checkout sessions working while they still
        // point at the legacy pathname-suffix callback.
        normalizedPathname.includes('failurecheckout')
    );
}
